import { useCallback, useEffect, useState } from 'react'
import type { ListingSearchDrawerRequest } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useEventSubscription, useTripPlannerEventBus } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { RightPanelView } from './useRightPanelState'

interface UseExploreMapReturnResult {
  exploreMapReturn: ListingSearchDrawerRequest | undefined
  returnToListingSearch: () => void
}

/** A reset landing on the map again is not a view change, so the effect below cannot see it. */
export function useExploreMapReturn(rightPanelView: RightPanelView): UseExploreMapReturnResult {
  const eventBus = useTripPlannerEventBus()
  const [exploreMapReturn, setExploreMapReturn] = useState<ListingSearchDrawerRequest | undefined>(undefined)

  useEventSubscription('show_explore_on_map', (event) => {
    setExploreMapReturn(event.returnToListingSearch)
  })

  useEventSubscription('reset_chat_state', () => {
    setExploreMapReturn(undefined)
  })

  // The map drops its explore state here, so the control would point at a search it has cleared.
  useEventSubscription('itinerary_changed', () => {
    setExploreMapReturn(undefined)
  })

  // The toggle, the assistant and the cart all land here, so one effect covers every way out.
  useEffect(() => {
    if (rightPanelView !== 'map') {
      setExploreMapReturn(undefined)
    }
  }, [rightPanelView])

  const returnToListingSearch = useCallback(() => {
    if (!exploreMapReturn) return
    setExploreMapReturn(undefined)
    eventBus.dispatch({ type: 'open_listing_search_drawer', ...exploreMapReturn })
  }, [eventBus, exploreMapReturn])

  return { exploreMapReturn, returnToListingSearch }
}
